/**
 * Тариф «по счёту» (docs/billing/plan.md §4): места сверх оплаченных и доплата за них.
 *
 * Модель — как true-up у Figma: участника сверх договора пускаем сразу, а место,
 * добавленное посреди срока, стоит по прайсу от даты добавления до конца оплаченного
 * срока. Считаем по максимуму мест за срок: ушёл один, пришёл другой — это то же
 * место, второй раз за него не берём. Доплату гасит либо разовый платёж картой, либо
 * админ, подняв число оплаченных мест после нового счёта.
 *
 * Чистый расчёт без БД — общий для сервера (сумма платежа) и веба (подсказки).
 */
import {
  PLANS,
  seatPriceRub,
  type BillingInterval,
  type BillingPeriodState,
  type PlanId,
} from './plans.js';
import { annualSeatCostRub, round2 } from './annual.js';

/**
 * Идёт оплаченный по счёту срок? Это `manual` до `paid_until` (бессрочный — всегда), а
 * ещё строка, где клиент по ходу срока включил продление картой: она уже `cloudpayments`
 * с подпиской от `paid_until`, но до первого списания срок по-прежнему оплачен счётом —
 * доплата за места и повышение тарифа считаются так же, как у ручного договора.
 */
export function isInvoiceTermActive(
  billing: BillingPeriodState | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!billing || billing.status === 'expired') {
    return false;
  }
  if (billing.source === 'manual') {
    // Ровно до `paid_until`, без запаса: в запасные дни тариф ещё живёт
    // (`isBillingPeriodExpired`), но срок закончился — доплаты за места и подъёмы тарифа
    // считаются только внутри него.
    return !billing.paidUntil || new Date(billing.paidUntil).getTime() >= now;
  }
  if (billing.source === 'cloudpayments' && billing.paidUntil) {
    return new Date(billing.paidUntil).getTime() >= now;
  }
  return false;
}

/** Средний месяц: срок по счёту бывает любой длины, цена места — за месяц. */
export const AVG_MONTH_MS = (365.25 / 12) * 86_400_000;

/** Интервал платности участника (`billing_seat_events`). */
export interface SeatIntervalLike {
  becameBillableAt: Date;
  ceasedBillableAt: Date | null;
}

/** Момент, когда мест стало больше, чем оплачено и чем было на пике до этого. */
export interface SeatTrueUpItem {
  at: Date;
  /** Сколько мест добавилось сверх прежнего пика. */
  addedSeats: number;
  /** Пик мест после добавления. */
  totalSeats: number;
  /** null — у тарифа нет цены места («Компания») или у срока нет конца. */
  amountRub: number | null;
}

export interface SeatTrueUp {
  /** Максимум платных мест за срок, но не меньше оплаченных. */
  peakSeats: number;
  overSeats: number;
  items: SeatTrueUpItem[];
  /** Сумма доплаты; null — посчитать в рублях нельзя (см. `SeatTrueUpItem.amountRub`). */
  trueUpRub: number | null;
}

/**
 * Цена места за месяц у тарифа по счёту (или у годовой подписки — месячная доля годовой
 * цены); null — тариф не посеточный.
 */
export function invoiceSeatPriceRub(
  plan: PlanId,
  seats: number,
  interval: BillingInterval = 'month',
): number | null {
  return PLANS[plan].pricing.kind === 'per_seat'
    ? seatPriceRub(plan, Math.max(1, seats), interval)
    : null;
}

/** Месячная сумма за `seats` мест по прайсу; null — тариф не посеточный. */
export function invoiceMonthlyRub(plan: PlanId, seats: number): number | null {
  const count = Math.max(1, seats);
  const price = invoiceSeatPriceRub(plan, count);
  return price === null ? null : round2(price * count);
}

/**
 * Доплата за места сверх оплаченных за срок `[termStart, termEnd]` по состоянию на `now`.
 *
 * Изменения состава в один и тот же момент применяются вместе: замена одного участника
 * другим (закрытие и открытие интервала одной транзакцией) пика не поднимает.
 *
 * Срок по счёту бывает любой длины, и место в нём стоит месячную цену за каждый средний
 * месяц до конца срока. У годовой подписки (`interval: 'year'`) место стоит долю годовой
 * цены — ту долю срока, что осталась с момента добавления: место с первого дня срока
 * стоит ровно годовую цену, а не «двенадцать средних месяцев» в високосный год.
 */
export function seatTrueUpFromIntervals(
  intervals: readonly SeatIntervalLike[],
  params: {
    plan: PlanId;
    paidSeats: number;
    termStart: Date;
    /** null — бессрочно: места считаем, рубли — нет. */
    termEnd: Date | null;
    now: Date;
    /** Цена по годовому прайсу и доля срока (годовая подписка); по умолчанию — счёт. */
    interval?: BillingInterval;
  },
): SeatTrueUp {
  const { plan, paidSeats } = params;
  const interval = params.interval ?? 'month';
  const start = params.termStart.getTime();
  const end = params.termEnd?.getTime() ?? null;
  const until = Math.min(params.now.getTime(), end ?? Number.POSITIVE_INFINITY);

  /** Сколько стоит одно место ступени `total` от `at` до конца срока; null — в рублях не считается. */
  const seatCostRub = (at: number, total: number): number | null => {
    if (end === null) {
      return null;
    }
    if (interval === 'year') {
      return annualSeatCostRub(plan, total, start, end, at);
    }
    const price = invoiceSeatPriceRub(plan, total);
    return price === null ? null : price * (Math.max(0, end - at) / AVG_MONTH_MS);
  };

  const items: SeatTrueUpItem[] = [];
  let peak = paidSeats;
  const push = (at: number, total: number) => {
    const added = total - peak;
    const cost = seatCostRub(at, total);
    const amountRub = cost === null ? null : round2(added * cost);
    items.push({ at: new Date(at), addedSeats: added, totalSeats: total, amountRub });
    peak = total;
  };

  if (until >= start) {
    let count = 0;
    const deltas = new Map<number, number>();
    for (const i of intervals) {
      const from = i.becameBillableAt.getTime();
      const to = i.ceasedBillableAt?.getTime() ?? null;
      if (from <= start && (to === null || to > start)) {
        count += 1;
      } else if (from > start && from <= until) {
        deltas.set(from, (deltas.get(from) ?? 0) + 1);
      }
      if (to !== null && to > start && to <= until && from <= until) {
        // Интервал, открытый после начала срока, закрывается своей же дельтой.
        deltas.set(to, (deltas.get(to) ?? 0) - 1);
      }
    }
    if (count > peak) {
      push(start, count);
    }
    for (const at of [...deltas.keys()].sort((a, b) => a - b)) {
      count += deltas.get(at)!;
      if (count > peak) {
        push(at, count);
      }
    }
  }

  const amounts = items.map((i) => i.amountRub);
  const trueUpRub =
    items.length === 0
      ? end === null || invoiceSeatPriceRub(plan, peak) === null
        ? null
        : 0
      : amounts.every((a): a is number => a !== null)
        ? round2(amounts.reduce((sum, a) => sum + a, 0))
        : null;

  return { peakSeats: peak, overSeats: peak - paidSeats, items, trueUpRub };
}

/**
 * Состояние тарифа по счёту для страницы тарифов и админки. `null` в `paidSeats` —
 * число мест договором не ограничено, доплаты нет.
 */
export interface InvoicePlanSummary {
  paidFrom: string | null;
  paidUntil: string | null;
  paidSeats: number | null;
  /** Платных мест сейчас. */
  billableSeats: number;
  peakSeats: number;
  overSeats: number;
  items: Array<{ at: string; addedSeats: number; totalSeats: number; amountRub: number | null }>;
  /** Доплата за места сверх оплаченных до конца срока; null — в рублях не считается. */
  trueUpRub: number | null;
  /** Цена месяца за нынешний состав по прайсу — база следующего счёта. */
  nextMonthlyRub: number | null;
  /** Цена места на ступени нынешнего состава. */
  seatPriceRub: number | null;
}
